import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  FolderKanban,
  TrendingUp,
  ArrowRight,
  Filter
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

export default function DashboardPage() {
  const { user, token, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setProjects(await response.json());
      }
    } catch (error) {
      console.error("Failed to load projects");
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const url = selectedProject === "all" 
        ? `${API}/dashboard/stats`
        : `${API}/dashboard/stats?project_id=${selectedProject}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  const statusColors = {
    pending: "#d97706",
    in_progress: "#2563eb", 
    completed: "#059669"
  };

  const pieData = stats ? [
    { name: "Pending", value: stats.pending, color: statusColors.pending },
    { name: "In Progress", value: stats.in_progress, color: statusColors.in_progress },
    { name: "Completed", value: stats.completed, color: statusColors.completed }
  ].filter(d => d.value > 0) : [];

  const deptData = stats ? Object.entries(stats.department_stats).map(([dept, data]) => ({
    name: dept.length > 10 ? dept.slice(0, 10) + '...' : dept,
    fullName: dept,
    completed: data.completed,
    pending: data.total - data.completed
  })) : [];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header with Filter */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 ">
            {user?.role === 'admin' ? 'Dashboard' : 'My Dashboard'}
          </h1>
          <p className="text-slate-500 mt-2">
            {user?.role === 'admin' 
              ? 'Overview of all preopening projects'
              : `Welcome back, ${user?.name}`
            }
          </p>
        </div>
        
        {/* Project Filter */}
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5 text-slate-400" />
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-[220px] rounded-lg border-slate-200 bg-white" data-testid="project-filter">
              <SelectValue placeholder="Filter by project" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="all" className="hover:bg-slate-100">All Projects</SelectItem>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id} className="hover:bg-slate-100">
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200 card-shadow card-shadow-hover transition-shadow" data-testid="projects-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Projects</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_projects || 0}</p>
              </div>
              <div className="p-3 bg-[#1e3a5f]/10 rounded-xl">
                <FolderKanban className="w-6 h-6 text-[#1e3a5f]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 card-shadow card-shadow-hover transition-shadow" data-testid="total-tasks-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Tasks</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_tasks || 0}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 card-shadow card-shadow-hover transition-shadow" data-testid="completed-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Completed</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1">{stats?.completed || 0}</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 card-shadow card-shadow-hover transition-shadow" data-testid="overdue-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Overdue</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{stats?.overdue || 0}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overall Completion */}
        <Card className="bg-white border-slate-200 card-shadow" data-testid="completion-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#1e3a5f]" />
              Overall Completion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center">
              <div className="text-5xl font-bold text-[#1e3a5f] mb-2">
                {stats?.completion_rate || 0}%
              </div>
              <p className="text-slate-500 text-sm mb-4">
                {stats?.completed || 0} of {stats?.total_tasks || 0} tasks completed
              </p>
              {pieData.length > 0 && (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Department Progress */}
        <Card className="bg-white border-slate-200 card-shadow" data-testid="dept-stats-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Department Progress</CardTitle>
          </CardHeader>
          <CardContent>
            {deptData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={deptData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#64748b" />
                  <YAxis dataKey="name" type="category" width={80} stroke="#64748b" fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="completed" fill="#059669" name="Completed" stackId="a" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="pending" fill="#d97706" name="Pending" stackId="a" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-500 text-center py-8">No department data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Projects List */}
      <Card className="bg-white border-slate-200 card-shadow" data-testid="projects-list-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <FolderKanban className="w-5 h-5 text-[#1e3a5f]" />
            Projects Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.project_stats?.length > 0 ? (
            <div className="space-y-4">
              {stats.project_stats.map((project, index) => (
                <div 
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-[#1e3a5f]/30 hover:bg-slate-100/50 cursor-pointer transition-all group"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-slate-900 group-hover:text-[#1e3a5f] transition-colors truncate">
                          {project.name}
                        </h3>
                        <Badge 
                          variant="outline" 
                          className={`rounded-md shrink-0 ${
                            project.rate === 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            project.rate > 50 ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            'bg-slate-100 text-slate-600 border-slate-200'
                          }`}
                        >
                          {project.rate}%
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-slate-500">
                        <span>{project.product}</span>
                        <span>•</span>
                        <span>{project.location}</span>
                        <span>•</span>
                        <span>{project.completed}/{project.total} tasks</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2 sm:mt-0">
                      <div className="flex-1 sm:w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#1e3a5f] rounded-full transition-all duration-500"
                          style={{ width: `${project.rate}%` }}
                        />
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#1e3a5f] transition-colors shrink-0" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No projects yet. Create your first project!</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
